
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEye,
  faBullseye,
  faShieldAlt,
  faUsers,
  faNewspaper,
  faBullhorn,
  faPhone,
  faEnvelope,
  faGlobe,
  faCheckCircle,
  faStar,
  faHeart,
  faRocket
} from '@fortawesome/free-solid-svg-icons';

const About = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const staggerItem = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 },
    transition: { duration: 0.5 }
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-white to-blue-50 pt-20 pb-8 px-2 sm:px-4">
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: isVisible ? 1 : 0, scale: isVisible ? 1 : 0.95 }}
        transition={{ duration: 0.8 }}
        className="w-full max-w-7xl mx-auto mb-12"
      >
        <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 p-8 sm:p-12 md:p-16 lg:p-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center mb-8"
          >
            <div className="inline-block bg-gradient-to-r from-red-500 to-red-600 text-white px-6 py-2 rounded-full text-sm font-semibold mb-4 shadow-lg">
              ABOUT US
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6">
              Welcome to{' '}
              <span className="bg-gradient-to-r from-red-500 to-red-600 bg-clip-text text-transparent">
                IINSAF
              </span>
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-red-500 to-red-600 mx-auto rounded-full mb-6"></div>
            <p className="text-lg sm:text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              A platform created to amplify the voices of people and provide opportunities for both{' '}
              <span className="font-semibold text-blue-600">Reporters</span> and{' '}
              <span className="font-semibold text-green-600">Advertisers</span>. Our mission is to connect genuine news reporters with trusted advertisers, ensuring that truth and opportunities reach everyone.
            </p>
          </motion.div>
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="w-full max-w-7xl mx-auto">
        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="grid grid-cols-1 lg:grid-cols-2 gap-8"
        >
          {/* Vision Section */}
          <motion.div
            variants={staggerItem}
            className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-6 sm:p-8 hover:shadow-2xl transition-all duration-300"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mr-4 shadow-lg">
                <FontAwesomeIcon icon={faEye} className="text-white text-xl" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">Our Vision</h2>
            </div>
            <div className="space-y-4">
              <p className="text-gray-600 text-lg leading-relaxed">
                To build a transparent, safe, and impactful digital ecosystem where:
              </p>
              <div className="space-y-3">
                <div className="flex items-start">
                  <FontAwesomeIcon icon={faNewspaper} className="text-blue-500 mt-1 mr-3" />
                  <span className="text-gray-700">Citizens' voices are heard through responsible journalism.</span>
                </div>
                <div className="flex items-start">
                  <FontAwesomeIcon icon={faUsers} className="text-green-500 mt-1 mr-3" />
                  <span className="text-gray-700">Reporters can earn fairly by sharing verified news and advertisements.</span>
                </div>
                <div className="flex items-start">
                  <FontAwesomeIcon icon={faBullseye} className="text-purple-500 mt-1 mr-3" />
                  <span className="text-gray-700">Advertisers can reach the right audience with genuine marketing.</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Mission Section */}
          <motion.div
            variants={staggerItem}
            className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-6 sm:p-8 hover:shadow-2xl transition-all duration-300"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-xl flex items-center justify-center mr-4 shadow-lg">
                <FontAwesomeIcon icon={faRocket} className="text-white text-xl" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">Our Mission</h2>
            </div>
            <div className="space-y-4">
              <div className="flex items-start">
                <FontAwesomeIcon icon={faNewspaper} className="text-green-500 mt-1 mr-3" />
                <span className="text-gray-700">Supporting independent reporters with verified content and earning opportunities.</span>
              </div>
              <div className="flex items-start">
                <FontAwesomeIcon icon={faBullhorn} className="text-blue-500 mt-1 mr-3" />
                <span className="text-gray-700">Providing advertisers a platform for trustworthy and effective marketing.</span>
              </div>
              <div className="flex items-start">
                <FontAwesomeIcon icon={faShieldAlt} className="text-purple-500 mt-1 mr-3" />
                <span className="text-gray-700">Ensuring data privacy, transparency, and a safe environment for all users.</span>
              </div>
            </div>
          </motion.div>

          {/* What We Do Section */}
          <motion.div
            variants={staggerItem}
            className="lg:col-span-2 bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-6 sm:p-8 hover:shadow-2xl transition-all duration-300"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl flex items-center justify-center mr-4 shadow-lg">
                <FontAwesomeIcon icon={faUsers} className="text-white text-xl" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">What We Do</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
                <div className="flex items-center mb-4">
                  <FontAwesomeIcon icon={faNewspaper} className="text-blue-600 text-2xl mr-3" />
                  <h3 className="text-xl font-bold text-blue-800">For Reporters</h3>
                </div>
                <p className="text-blue-700 leading-relaxed">
                  We provide opportunities to publish verified news and advertisements while earning money for their efforts.
                </p>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border border-green-200">
                <div className="flex items-center mb-4">
                  <FontAwesomeIcon icon={faBullhorn} className="text-green-600 text-2xl mr-3" />
                  <h3 className="text-xl font-bold text-green-800">For Advertisers</h3>
                </div>
                <p className="text-green-700 leading-relaxed">
                  We offer a secure panel where advertisers can submit their ads, which are reviewed and then shared with verified reporters for wider reach.
                </p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border border-purple-200">
                <div className="flex items-center mb-4">
                  <FontAwesomeIcon icon={faUsers} className="text-purple-600 text-2xl mr-3" />
                  <h3 className="text-xl font-bold text-purple-800">For Readers</h3>
          </div>
                <p className="text-purple-700 leading-relaxed">
                  We deliver authentic and responsible news that truly represents the people's voice.
                </p>
          </div>
          </div>
          </motion.div>

          {/* Values Section */}
          <motion.div
            variants={staggerItem}
            className="lg:col-span-2 bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-6 sm:p-8 hover:shadow-2xl transition-all duration-300"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl flex items-center justify-center mr-4 shadow-lg">
                <FontAwesomeIcon icon={faStar} className="text-white text-xl" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">Our Values</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl border border-blue-200">
                <FontAwesomeIcon icon={faEye} className="text-blue-600 mt-1 mr-4" />
                <div>
                  <h3 className="font-bold text-blue-800 mb-2">Transparency</h3>
                  <p className="text-blue-700">Clear policies and fair opportunities for everyone.</p>
                </div>
              </div>
              <div className="flex items-start p-4 bg-gradient-to-r from-green-50 to-green-100 rounded-xl border border-green-200">
                <FontAwesomeIcon icon={faShieldAlt} className="text-green-600 mt-1 mr-4" />
                <div>
                  <h3 className="font-bold text-green-800 mb-2">Integrity</h3>
                  <p className="text-green-700">Strict action against fraud, scams, or misuse.</p>
                </div>
              </div>
              <div className="flex items-start p-4 bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl border border-purple-200">
                <FontAwesomeIcon icon={faRocket} className="text-purple-600 mt-1 mr-4" />
                <div>
                  <h3 className="font-bold text-purple-800 mb-2">Empowerment</h3>
                  <p className="text-purple-700">Giving people, reporters, and advertisers a chance to grow.</p>
                </div>
              </div>
              <div className="flex items-start p-4 bg-gradient-to-r from-orange-50 to-orange-100 rounded-xl border border-orange-200">
                <FontAwesomeIcon icon={faShieldAlt} className="text-orange-600 mt-1 mr-4" />
                <div>
                  <h3 className="font-bold text-orange-800 mb-2">Security</h3>
                  <p className="text-orange-700">Protecting user data and ensuring a safe platform.</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Why Choose IINSAF Section */}
          <motion.div
            variants={staggerItem}
            className="lg:col-span-2 bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-6 sm:p-8 hover:shadow-2xl transition-all duration-300"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-red-600 rounded-xl flex items-center justify-center mr-4 shadow-lg">
                <FontAwesomeIcon icon={faHeart} className="text-white text-xl" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">Why Choose IINSAF?</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                "Verified news and advertisements",
                "Safe and secure payment system through Razorpay",
                "Wallet-based refund and reuse policy",
                "User-friendly, transparent, and community-driven platform",
                "Strict guidelines against fraud, scams, and objectionable content"
              ].map((item, index) => (
                <div key={index} className="flex items-center p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-200 hover:shadow-md transition-all duration-300">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3" />
                  <span className="text-gray-700 font-medium">{item}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Contact Section */}
          <motion.div
            variants={staggerItem}
            className="lg:col-span-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl shadow-xl p-6 sm:p-8 text-white"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mr-4 shadow-lg">
                <FontAwesomeIcon icon={faPhone} className="text-white text-xl" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold">Contact Us</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-center p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
                <FontAwesomeIcon icon={faPhone} className="text-white text-xl mr-4" />
                <div>
                  <p className="font-semibold">Phone</p>
                  <p className="text-white/90">9992396623</p>
                </div>
              </div>
              <div className="flex items-center p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
                <FontAwesomeIcon icon={faEnvelope} className="text-white text-xl mr-4" />
                <div>
                  <p className="font-semibold">Email</p>
                  <a href="mailto:iinsafofficial@gmail.com" className="text-white/90 hover:text-white transition-colors duration-300">
                    iinsafofficial@gmail.com
                  </a>
                </div>
              </div>
              <div className="flex items-center p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
                <FontAwesomeIcon icon={faGlobe} className="text-white text-xl mr-4" />
                <div>
                  <p className="font-semibold">Website</p>
                  <a href="https://www.iinsaf.com" className="text-white/90 hover:text-white transition-colors duration-300" target="_blank" rel="noopener noreferrer">
                    www.iinsaf.com
                  </a>
                </div>
              </div>
          </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default About;